import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { ApiError } from '@/api/client';
import { useRegister } from '@/api/queries/auth';
import { Button, Field, SelectField } from '@pixlide/ui';
import { AuthLayout, FormAlert } from '@/features/auth/AuthLayout';
import { DialCodeSelect } from '@/features/auth/DialCodeSelect';
import { PasswordPolicy } from '@/features/auth/PasswordPolicy';
import { SocialAuthButtons } from '@/features/auth/SocialAuthButtons';
import { COUNTRIES, DIAL_CODES, JOB_TITLES, OTHER_JOB_TITLE } from '@/features/auth/job-titles';
import {
  registerCredentialsSchema,
  registerProfileSchema,
  type RegisterCredentialsInput,
  type RegisterProfileInput,
} from '@/features/auth/schemas';

const EMPTY_CREDENTIALS: RegisterCredentialsInput = {
  email: '',
  password: '',
  confirmPassword: '',
};

/**
 * Champs de l'étape 2 pouvant recevoir une erreur de validation du serveur.
 * Ceux qui n'y figurent pas (email, mot de passe) appartiennent à l'étape précédente et
 * sont affichés en bandeau, faute de champ où les rattacher.
 */
const PROFILE_FIELDS = {
  firstName: true,
  lastName: true,
  jobTitle: true,
  companyName: true,
  phoneNumber: true,
  websiteUrl: true,
  city: true,
  countryCode: true,
} as const;

/** Un champ facultatif laissé vide vaut « non renseigné », pas « chaîne vide ». */
function emptyToNull(value: string | undefined): string | null {
  const trimmed = (value ?? '').trim();

  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Inscription en deux étapes, comme sur la maquette : d'abord les identifiants, ensuite
 * l'identité et l'établissement.
 *
 * Le découpage n'est pas cosmétique. Un formulaire unique de six champs présenté d'emblée
 * fait abandonner ; deux écrans courts affichent la même charge de travail en la rendant
 * franchissable. La création du compte n'a lieu qu'à la fin, en un seul appel : il n'existe
 * donc jamais de compte à moitié créé si l'utilisateur s'arrête entre les deux étapes.
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const registerAccount = useRegister();

  const [step, setStep] = useState<'credentials' | 'profile'>('credentials');

  // Les identifiants survivent au passage à l'étape 2 pour être renvoyés tels quels au
  // retour arrière. Ils ne quittent pas la mémoire de l'onglet — ni stockage, ni URL.
  const [credentials, setCredentials] = useState<RegisterCredentialsInput>(EMPTY_CREDENTIALS);

  if (step === 'credentials') {
    return (
      <CredentialsStep
        defaultValues={credentials}
        onSubmitted={(values) => {
          setCredentials(values);
          setStep('profile');
        }}
      />
    );
  }

  return (
    <ProfileStep
      credentials={credentials}
      onBack={() => setStep('credentials')}
      onSubmitted={async (profile) => {
        const account = await registerAccount.mutateAsync({
          email: credentials.email,
          password: credentials.password,
          firstName: profile.firstName,
          lastName: profile.lastName,
          companyName: profile.companyName,

          // « Autre » n'est pas un poste : c'est ce que l'utilisateur a écrit qui est envoyé.
          jobTitle:
            profile.jobTitle === OTHER_JOB_TITLE
              ? (profile.customJobTitle ?? '').trim()
              : profile.jobTitle,

          // L'indicatif est recollé au numéro : le serveur stocke une chaîne composable
          // telle quelle dans un lien « tel: ».
          phoneNumber: `${profile.dialCode} ${profile.phoneNumber.trim()}`,

          websiteUrl: emptyToNull(profile.websiteUrl),
          city: emptyToNull(profile.city),
          countryCode: profile.countryCode,
        });

        // Le compte existe, il reste à prouver la possession de l'adresse. La confirmation
        // a sa propre URL : elle survit ainsi à un rechargement, alors qu'un simple état de
        // cette page enfermait l'utilisateur — l'adresse étant désormais prise, il ne
        // pouvait ni se réinscrire ni se connecter.
        await navigate({ to: '/confirmation-email', search: { email: account.email } });
      }}
      error={registerAccount.error}
      isPending={registerAccount.isPending}
    />
  );
}

// ============================================================================
// Étape 1 — identifiants
// ============================================================================

function CredentialsStep({
  defaultValues,
  onSubmitted,
}: {
  defaultValues: RegisterCredentialsInput;
  onSubmitted: (values: RegisterCredentialsInput) => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterCredentialsInput>({
    resolver: zodResolver(registerCredentialsSchema),
    defaultValues,
  });

  return (
    <AuthLayout
      tab="register"
      title="Créer un compte"
      subtitle="Lancez-vous avec Pixlide dès aujourd'hui."
    >
      <form
        onSubmit={handleSubmit(onSubmitted)}
        noValidate
        className="flex flex-col gap-3"
      >
        <Field
          label="Adresse email"
          type="email"
          autoComplete="email"
          placeholder="monadresse@mail.com"
          autoFocus
          required
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          label="Mot de passe"
          type="password"
          revealable
          autoComplete="new-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />

        <PasswordPolicy password={watch('password')} email={watch('email')} />

        <Field
          label="Confirmez le mot de passe"
          type="password"
          revealable
          autoComplete="new-password"
          required
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        {/* Pas de `isLoading` : cette étape ne fait aucun appel réseau, elle ne fait
            qu'ouvrir la suivante. */}
        <Button type="submit" className="mt-2 h-11 w-full rounded-xl">
          Continuer
          <ArrowRight aria-hidden className="size-4" />
        </Button>
      </form>

      <p className="mt-4 text-[11px] text-ink-500">
        Vous avez déjà un compte ?{' '}
        <Link to="/connexion" search={{}} className="font-semibold text-ink-900 hover:underline">
          Se connecter
        </Link>
      </p>

      <div className="mt-5">
        <SocialAuthButtons />
      </div>
    </AuthLayout>
  );
}

// ============================================================================
// Étape 2 — identité et établissement
// ============================================================================

function ProfileStep({
  credentials,
  onBack,
  onSubmitted,
  error,
  isPending,
}: {
  credentials: RegisterCredentialsInput;
  onBack: () => void;
  onSubmitted: (values: RegisterProfileInput) => Promise<void>;
  error: unknown;
  isPending: boolean;
}) {
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterProfileInput>({
    resolver: zodResolver(registerProfileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      jobTitle: '',
      customJobTitle: '',
      companyName: '',
      dialCode: DIAL_CODES[0].code,
      phoneNumber: '',
      websiteUrl: '',
      city: '',
      countryCode: 'FR',
    },
  });

  const needsCustomJobTitle = watch('jobTitle') === OTHER_JOB_TITLE;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await onSubmitted(values);
    } catch (submitError) {
      // Les erreurs de validation du SERVEUR sont réinjectées champ par champ dans le
      // formulaire. Le serveur renvoie les clés en camelCase, exactement les noms des
      // champs du formulaire : aucune table de correspondance n'est nécessaire.
      if (submitError instanceof ApiError && submitError.hasFieldErrors) {
        for (const [field, messages] of Object.entries(submitError.errors ?? {})) {
          if (field in values && messages[0] !== undefined) {
            setError(field as keyof RegisterProfileInput, { type: 'server', message: messages[0] });
          }
        }
      }
    }
  });

  const apiError = error instanceof ApiError ? error : null;

  // Une erreur qui porte sur l'email ou le mot de passe ne peut être corrigée qu'à l'étape
  // précédente : elle est donc affichée en bandeau, à côté du bouton de retour, plutôt que
  // rattachée à un champ absent de cet écran.
  const hasFieldErrorHere =
    apiError?.hasFieldErrors === true &&
    Object.keys(apiError.errors ?? {}).some((field) => field in PROFILE_FIELDS);

  const alertMessage = apiError === null || hasFieldErrorHere ? null : apiError.message;

  return (
    <AuthLayout
      tab="register"
      title="Créer un compte"
      subtitle="Lancez-vous avec Pixlide dès aujourd'hui."
    >
      {alertMessage ? <FormAlert message={alertMessage} /> : null}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Prénom"
            autoComplete="given-name"
            autoFocus
            required
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <Field
            label="Nom"
            autoComplete="family-name"
            required
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </div>

        <SelectField
          label="Poste occupé"
          required
          error={errors.jobTitle?.message}
          {...register('jobTitle')}
        >
          <option value="">Sélectionnez votre poste</option>
          {JOB_TITLES.map((title) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
          <option value={OTHER_JOB_TITLE}>{OTHER_JOB_TITLE}</option>
        </SelectField>

        {needsCustomJobTitle ? (
          <Field
            label="Intitulé de votre poste"
            required
            placeholder="Notez l’intitulé de votre poste"
            error={errors.customJobTitle?.message}
            {...register('customJobTitle')}
          />
        ) : null}

        {/* Entreprise et téléphone sur une même ligne, comme sur la maquette : ils forment
            la carte de visite du compte et se lisent ensemble. */}
        {/* Colonnes inégales : « Entreprise » se lit d'un coup d'œil, alors qu'un numéro
            de téléphone tronqué ne se relit pas. La place va donc au second. */}
        <div className="grid grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] gap-3">
          <Field
            label="Entreprise"
            autoComplete="organization"
            required
            error={errors.companyName?.message}
            {...register('companyName')}
          />

          <div className="flex items-stretch gap-2">
            <DialCodeSelect {...register('dialCode')} />

            <Field
              label="N° téléphone"
              type="tel"
              autoComplete="tel-national"
              required
              className="min-w-0 flex-1"
              placeholder="04 22 11 22 11"
              error={errors.phoneNumber?.message}
              {...register('phoneNumber')}
            />
          </div>
        </div>

        <Field
          label="Site web"
          type="url"
          autoComplete="url"
          prefix="https://"
          placeholder="pitaya.fr"
          error={errors.websiteUrl?.message}
          {...register('websiteUrl')}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Ville"
            autoComplete="address-level2"
            placeholder="Bordeaux"
            error={errors.city?.message}
            {...register('city')}
          />

          <SelectField label="Pays" error={errors.countryCode?.message} {...register('countryCode')}>
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.label}
              </option>
            ))}
          </SelectField>
        </div>

        <Button
          type="submit"
          isLoading={isSubmitting || isPending}
          className="mt-2 h-11 w-full rounded-xl"
        >
          Créer un compte
          <ArrowRight aria-hidden className="size-4" />
        </Button>

        {/* L'adresse est rappelée ici : c'est la seule information de l'étape précédente
            encore en jeu, et la corriger après coup demanderait sinon de tout recommencer. */}
        <button
          type="button"
          onClick={onBack}
          className="mx-auto mt-1 flex items-center gap-1.5 text-[11px] text-ink-500 transition hover:text-ink-900"
        >
          <ArrowLeft aria-hidden className="size-3.5" />
          Modifier mes identifiants ({credentials.email})
        </button>
      </form>
    </AuthLayout>
  );
}
